'use client';

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'temp_bookmarks';

interface Bookmark {
  id: string;
  type: 'service' | 'supplier';
  addedAt: number;
}

export function useTemporaryBookmarks() {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [isClient, setIsClient] = useState(false);

  // Load from localStorage on mount (client-side only)
  useEffect(() => {
    setIsClient(true);
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setBookmarks(parsed);
      } catch (error) {
        console.error('Error parsing bookmarks:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Save to localStorage whenever bookmarks change
  useEffect(() => {
    if (isClient) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(bookmarks));
    }
  }, [bookmarks, isClient]);

  const addBookmark = (id: string, type: 'service' | 'supplier') => {
    setBookmarks((prev) => {
      // Check if already bookmarked
      if (prev.some((b) => b.id === id && b.type === type)) {
        return prev;
      }

      return [
        ...prev,
        {
          id,
          type,
          addedAt: Date.now(),
        },
      ];
    });
  };

  const removeBookmark = (id: string, type: 'service' | 'supplier') => {
    setBookmarks((prev) => prev.filter((b) => !(b.id === id && b.type === type)));
  };

  const toggleBookmark = (id: string, type: 'service' | 'supplier') => {
    if (isBookmarked(id, type)) {
      removeBookmark(id, type);
    } else {
      addBookmark(id, type);
    }
  };

  const isBookmarked = (id: string, type: 'service' | 'supplier') => {
    return bookmarks.some((b) => b.id === id && b.type === type);
  };

  const clearAll = () => {
    setBookmarks([]);
    if (isClient) {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const getBookmarksByType = (type: 'service' | 'supplier') => {
    return bookmarks.filter((b) => b.type === type);
  };

  return {
    bookmarks,
    addBookmark,
    removeBookmark,
    toggleBookmark,
    isBookmarked,
    clearAll,
    getBookmarksByType,
    count: bookmarks.length,
    isClient,
  };
}
